import { Mark } from '@tiptap/core'
import { ReactMarkViewRenderer } from '@tiptap/react'
import {
  MISSING_FIELD_PLACEHOLDER_REGEXES,
  NOT_FOUND_PLACEHOLDER,
  parsePlaceholderColonContent,
} from '@artem-lex/compliance-documents'
import { NotSpecifiedMarkView } from './NotSpecifiedMarkView'

/**
 * Re-export for callers that import from this module (must stay aligned with
 * server sectionContentConstants for the same string literals).
 */
export {
  LEGACY_NOT_FOUND_IN_DOCUMENTS_REGEX,
  LEGACY_NOT_SPECIFIED_REGEX,
  MISSING_FIELD_PLACEHOLDER_REGEXES,
  NOT_FOUND_PLACEHOLDER,
  NOT_FOUND_REGEX,
} from '@artem-lex/compliance-documents'

export const NOT_SPECIFIED_MARK = 'notSpecified'

export const NOT_SPECIFIED_PLACEHOLDERS = [NOT_FOUND_PLACEHOLDER] as const

export interface PlaceholderMatch {
  index: number
  length: number
  text: string
  placeholder: string
  fieldLabel: string | null
  guidance: string | null
}

/**
 * Find all missing-field placeholder matches in a text string, sorted by index.
 * Returns non-overlapping matches; longer matches are preferred when they start at the same index.
 */
export function findPlaceholderMatches(text: string): PlaceholderMatch[] {
  const matches: PlaceholderMatch[] = []

  for (const regex of MISSING_FIELD_PLACEHOLDER_REGEXES) {
    regex.lastIndex = 0
    let match: RegExpExecArray | null
    while ((match = regex.exec(text)) !== null) {
      const raw = match[1] ?? null
      const parsed = raw ? parsePlaceholderColonContent(raw) : null
      matches.push({
        index: match.index,
        length: match[0].length,
        text: match[0],
        placeholder: NOT_FOUND_PLACEHOLDER,
        fieldLabel: parsed?.fieldLabel ?? null,
        guidance: parsed?.guidance ?? null,
      })
    }
  }

  matches.sort((aa, bb) => aa.index - bb.index || bb.length - aa.length)

  const result: PlaceholderMatch[] = []
  let lastEnd = -1
  for (const pm of matches) {
    if (pm.index >= lastEnd) {
      result.push(pm)
      lastEnd = pm.index + pm.length
    }
  }

  return result
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    notSpecified: {
      setNotSpecified: (attributes?: {
        placeholder: string
        fieldLabel?: string | null
        guidance?: string | null
      }) => ReturnType
      unsetNotSpecified: () => ReturnType
    }
  }
}

export const NotSpecifiedMark = Mark.create({
  name: NOT_SPECIFIED_MARK,
  /**
   * Avoids inclusive marks re-applying to the next typed character after the placeholder
   * range is deleted (same ProseMirror stored-marks behavior as manual instructions).
   */
  inclusive: false,

  addOptions() {
    return {
      HTMLAttributes: {},
    }
  },

  addMarkView() {
    return ReactMarkViewRenderer(NotSpecifiedMarkView)
  },

  addAttributes() {
    return {
      placeholder: {
        default: NOT_FOUND_PLACEHOLDER,
        parseHTML: (element) =>
          (element as HTMLElement).getAttribute('data-placeholder') ??
          NOT_FOUND_PLACEHOLDER,
        renderHTML: (attributes) => {
          if (!attributes.placeholder) {
            return {}
          }
          return {
            'data-placeholder': attributes.placeholder,
          }
        },
      },
      fieldLabel: {
        default: null,
        parseHTML: (element) =>
          (element as HTMLElement).getAttribute('data-field-label') ?? null,
        renderHTML: (attributes) => {
          if (!attributes.fieldLabel) {
            return {}
          }
          return { 'data-field-label': attributes.fieldLabel }
        },
      },
      guidance: {
        default: null,
        parseHTML: (element) =>
          (element as HTMLElement).getAttribute('data-guidance') ?? null,
        renderHTML: (attributes) => {
          if (!attributes.guidance) {
            return {}
          }
          return { 'data-guidance': attributes.guidance }
        },
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-not-specified]',
        getAttrs: (element) => {
          const el = element as HTMLElement
          return {
            placeholder:
              el.getAttribute('data-placeholder') ?? NOT_FOUND_PLACEHOLDER,
            fieldLabel: el.getAttribute('data-field-label') ?? null,
            guidance: el.getAttribute('data-guidance') ?? null,
          }
        },
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      {
        class: 'not-specified',
        'data-not-specified': '',
        ...this.options.HTMLAttributes,
        ...HTMLAttributes,
      },
      0,
    ]
  },

  renderMarkdown: (node, _helpers) => {
    const fieldLabel = node.attrs?.fieldLabel as string | null
    const guidance = node.attrs?.guidance as string | null
    if (fieldLabel) {
      if (guidance) {
        return `[NOT_FOUND: ${fieldLabel} | ${guidance}]`
      }
      return `[NOT_FOUND: ${fieldLabel}]`
    }
    return NOT_FOUND_PLACEHOLDER
  },
})
